/**
 * Eyvo-branded PDF report generator.
 * Uses jsPDF + autoTable for professional tabular output.
 * Supports standard table, header-only, line-only, and combo card formats.
 * Fully generic — field names, values, and layout driven by config.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import eyvoLogo from '../../assets/eyvo_report_logo.png';
import { fmtCurrency, fmtNum, fmtDateTz, fmtDateOnlyTz, fmtCurrencyCode, fmtPct, isDateKey } from './reportUtils';

// ── Logo preload for PDF ────────────────────────────────────────────
let _logoDataUrl = null;
const _logoImg = new Image();
_logoImg.onload = () => {
  const c = document.createElement('canvas');
  c.width = _logoImg.naturalWidth; c.height = _logoImg.naturalHeight;
  c.getContext('2d').drawImage(_logoImg, 0, 0);
  _logoDataUrl = c.toDataURL('image/png');
};
_logoImg.src = eyvoLogo;
export { eyvoLogo };

/** Get value with fallbacks */
const _getVal = (row, key) => {
  if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  const fb = { delivery_point_code: ['delivery_code'], document_type_name: ['document_type'], invoice_point_name: ['invoice_code'], order_date: ['created_at'], issued_date: ['issue_date'], entry_date: ['created_at'] };
  for (const alt of (fb[key] || [])) { if (row[alt] !== undefined && row[alt] !== null && row[alt] !== '') return row[alt]; }
  return '';
};

/** Compute doc value from config valueFields */
const _docVal = (row, vf) => { for (const f of (vf || [])) { const v = row[f]; if (v !== undefined && v !== null && v !== '') return parseFloat(v); } return 0; };

/** Format cell for PDF based on column config */
const _fmtPdfCell = (val, col, row, tz) => {
  if (val === null || val === undefined || val === '') return '\u2014';
  if (col.currency || col.key === '_doc_value') return fmtCurrencyCode(val, row?._currency || row?.currency || 'USD');
  if (col.pct || col.key === 'discount_pct') return fmtPct(val);
  if (col.qty) return Number(val).toFixed(2);
  if (isDateKey(col.key) || ['order_date','issued_date','entry_date','invoice_date','due_date','received_date','item_due_date'].includes(col.key))
    return val ? fmtDateOnlyTz(val, tz) : '';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

function _drawPageFooter(doc, pageW, pageH, pageNum, year) {
  doc.setFontSize(7); doc.setTextColor(100);
  doc.text(`Page ${pageNum}`, pageW - 25, pageH - 8);
  doc.text(`\u00A9 ${year} Eyvo. All rights reserved.`, 14, pageH - 8);
  doc.setTextColor(80, 130, 200);
  doc.textWithLink('Powered by Eyvo eProcurement Solutions https://www.eyvo.com', pageW / 2 - 40, pageH - 8, { url: 'https://www.eyvo.com' });
  doc.setTextColor(0, 0, 0);
}

function _drawContPageHeader(doc, pageW, title, generatedAt) {
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(120);
  doc.text(`${title}  |  ${generatedAt}`, 14, 10);
  doc.setTextColor(0, 0, 0);
  if (_logoDataUrl) { try { const lH2 = 7; const asp2 = _logoImg.naturalWidth / _logoImg.naturalHeight; doc.addImage(_logoDataUrl, 'PNG', pageW - lH2 * asp2 - 14, 4, lH2 * asp2, lH2); } catch {} }
}

function _drawReportHeader(doc, pageW, title, tz) {
  const generatedAt = fmtDateTz(new Date().toISOString(), tz);
  let y = 14;
  doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  doc.text('Licensee :', 14, y); doc.setFont('helvetica', 'normal'); doc.text('Licensee', 42, y); y += 4;
  doc.setFont('helvetica', 'bold'); doc.text('Report Currency :', 14, y); doc.setFont('helvetica', 'normal'); doc.text('US Dollars', 48, y);
  doc.setFontSize(8); doc.text(generatedAt, pageW - 14, 14, { align: 'right' });
  y += 8;
  doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.text(title, 14, y); y += 6;
  return { y, generatedAt };
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN ENTRY POINT
// ═══════════════════════════════════════════════════════════════════════════

export default function generateReportPDF(title, columns, rows, summaryObj, tz, options = {}) {
  const { reportFormat, moduleLabel } = options;

  if (reportFormat) {
    const modName = moduleLabel || title?.split(' — ')?.[0] || 'Report';
    if (reportFormat.value === 'header_only') return _genFlatPDF(modName, rows, tz, reportFormat, '(Header Details Only)');
    if (reportFormat.value === 'line_only') return _genFlatPDF(modName, rows, tz, reportFormat, '(Line Details Only)');
    if (reportFormat.value === 'combo') return _genComboPDF(modName, rows, tz, reportFormat);
  }

  // ── Default/legacy table PDF ──
  const orientation = columns.length > 6 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation });
  const pageW = doc.internal.pageSize.getWidth();
  const year = new Date().getFullYear();
  const generatedAt = fmtDateTz(new Date().toISOString(), tz);

  doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.text(title, 14, 18);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.text(`Generated: ${generatedAt}`, 14, 24);
  if (_logoDataUrl) { try { const lH = 12; const asp = _logoImg.naturalWidth / _logoImg.naturalHeight; doc.addImage(_logoDataUrl, 'PNG', pageW - lH * asp - 14, 6, lH * asp, lH); } catch { doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59); doc.text('EYVO', pageW - 14, 15, { align: 'right' }); doc.setTextColor(0, 0, 0); } }

  let startY = 32;
  if (summaryObj && Object.keys(summaryObj).length > 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    Object.entries(summaryObj).filter(([k]) => k !== 'buckets').forEach(([k, v], i) => {
      const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const val = typeof v === 'number' && (k.includes('value') || k.includes('cost') || k.includes('nbv') || k.includes('paid') || k.includes('outstanding') || k.includes('payable') || k.includes('proceeds') || k.includes('savings') || k.includes('awarded') || k.includes('depreciation'))
        ? fmtCurrency(v) : fmtNum(v);
      doc.text(`${label}: ${val}`, 14, startY + i * 5);
    });
    startY += Object.keys(summaryObj).filter(k => k !== 'buckets').length * 5 + 4;
  }

  const SHORT_LABELS = { 'Created By User Id': 'Created By', 'Created By User': 'Created By' };
  const head = [columns.map(c => SHORT_LABELS[c.label] || c.label)];
  const body = rows.map(r => {
    const isLI = r._isLineItem || r._rowType === 'lineItem';
    return columns.map(c => {
      if (!isLI && c.key.startsWith('li_')) return '';
      let val = r[c.key];
      if (val === null || val === undefined) return '\u2014';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (isDateKey(c.key) && val) { try { return fmtDateTz(val, tz); } catch { return String(val); } }
      if (typeof val === 'number' && (c.key.includes('value') || c.key.includes('cost') || c.key.includes('price') || c.key.includes('total') || c.key.includes('paid') || c.key.includes('outstanding') || c.key.includes('proceeds') || c.key.includes('nbv') || c.key.includes('depr')))
        return fmtCurrency(val);
      return String(val);
    });
  });

  // Build column styles from user-defined widths
  const colStyles = {};
  columns.forEach((c, i) => { if (c.width) colStyles[i] = { cellWidth: c.width * 0.75 }; });

  let pageNum = 0;
  autoTable(doc, {
    head, body, startY,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: Object.keys(colStyles).length ? colStyles : undefined,
    margin: { left: 14, right: 14, top: 22 },
    didParseCell: (data) => {
      if (data.section === 'body' && rows[data.row]) {
        const r = rows[data.row];
        if (r._isLineItem || r._rowType === 'lineItem') { data.cell.styles.fontSize = 6; data.cell.styles.textColor = [100, 116, 139]; data.cell.styles.fillColor = [241, 245, 249]; }
      }
    },
    didDrawPage: () => { pageNum++; const pageH = doc.internal.pageSize.getHeight(); if (pageNum > 1) _drawContPageHeader(doc, pageW, title, generatedAt); _drawPageFooter(doc, pageW, pageH, pageNum, year); },
  });
  return doc;
}

// ═══════════════════════════════════════════════════════════════════════════
//  GENERIC FLAT TABLE PDF (header_only or line_only)
// ═══════════════════════════════════════════════════════════════════════════

function _genFlatPDF(modName, rows, tz, fmt, suffix) {
  const fullTitle = `${modName} Management Report ${suffix}`;
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageW = doc.internal.pageSize.getWidth();
  const year = new Date().getFullYear();
  const { y: startY, generatedAt } = _drawReportHeader(doc, pageW, fullTitle, tz);

  const lf = fmt.linesField || 'line_items';
  const excludeRe = fmt.excludeStatuses?.length ? new RegExp(fmt.excludeStatuses.join('|'), 'i') : null;

  let dataRows, totalVal = null;

  if (fmt.value === 'header_only') {
    dataRows = rows.map(r => ({ ...r, _line_count: (r[lf] || []).length, _doc_value: _docVal(r, fmt.valueFields) }));
    if (fmt.totalField) totalVal = dataRows.filter(r => !excludeRe || !excludeRe.test(r.status)).reduce((s, r) => s + _docVal(r, fmt.valueFields), 0);
  } else {
    // line_only — flatten
    dataRows = [];
    for (const r of rows) {
      for (const li of (r[lf] || [])) {
        const flat = { _currency: r.currency || 'USD', _isCancelled: excludeRe && excludeRe.test(r.status) };
        for (const pf of (fmt.parentFields || [])) flat[pf] = r[pf] || '';
        for (const k of Object.keys(li)) flat[k] = li[k];
        flat.discount_pct = li.discount || 0;
        dataRows.push(flat);
      }
    }
    if (fmt.totalField) totalVal = dataRows.filter(r => !r._isCancelled).reduce((s, r) => s + parseFloat(r[fmt.totalField] || 0), 0);
  }

  const cols = fmt.columns;
  const head = [cols.map(c => c.label)];
  const body = dataRows.map(r => cols.map(c => _fmtPdfCell(_getVal(r, c.key), c, r, tz)));

  // Build column styles from user-defined widths
  const columnStyles = {};
  cols.forEach((c, i) => { if (c.width) columnStyles[i] = { cellWidth: c.width * 0.75 }; }); // px to pt approx

  let pageNum = 0;
  autoTable(doc, {
    head, body, startY: startY + 2,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: Object.keys(columnStyles).length ? columnStyles : undefined,
    margin: { left: 14, right: 14, top: 22 },
    didDrawPage: () => { pageNum++; const pH = doc.internal.pageSize.getHeight(); if (pageNum > 1) _drawContPageHeader(doc, pageW, fullTitle, generatedAt); _drawPageFooter(doc, pageW, pH, pageNum, year); },
  });

  const lastY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(`Total Records: ${dataRows.length}`, 14, lastY);
  if (totalVal !== null && fmt.totalLabel) {
    doc.setFont('helvetica', 'bold');
    doc.text(fmt.totalLabel, pageW - 80, lastY);
    doc.text(totalVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), pageW - 14, lastY, { align: 'right' });
  }
  if (fmt.noteText) { doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.text(`Note: ${fmt.noteText}`, 14, lastY + 6); }
  return doc;
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMBO FORMAT PDF — card-based layout per record
// ═══════════════════════════════════════════════════════════════════════════

function _genComboPDF(modName, rows, tz, fmt) {
  const fullTitle = `${modName} Management Report`;
  const doc = new jsPDF({ orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const year = new Date().getFullYear();
  const margin = 14; const contentW = pageW - margin * 2;

  const { y: headerEndY, generatedAt } = _drawReportHeader(doc, pageW, fullTitle, tz);
  let curY = headerEndY + 4;
  let pageNum = 1;
  const bottomMargin = 20;

  const lf = fmt.linesField || 'line_items';
  const pkField = fmt.primaryKeyField || 'order_number';
  const pkLabel = fmt.primaryKeyLabel || 'Order Number';

  const checkNewPage = (needed) => {
    if (curY + needed > pageH - bottomMargin) {
      _drawPageFooter(doc, pageW, pageH, pageNum, year);
      doc.addPage(); pageNum++;
      _drawContPageHeader(doc, pageW, fullTitle, generatedAt);
      curY = 18; return true;
    }
    return false;
  };

  for (let oi = 0; oi < rows.length; oi++) {
    const rec = rows[oi];
    const currency = rec.currency || 'USD';
    const docValue = fmt.valueFields ? _docVal(rec, fmt.valueFields) : null;
    const lines = rec[lf] || [];

    const estHeight = 8 + 32 + (lines.length > 0 ? 10 + lines.length * 5 : 0) + (docValue !== null ? 10 : 0) + 8;
    checkNewPage(Math.min(estHeight, 80));

    // Header bar
    doc.setFillColor(230, 230, 230); doc.rect(margin, curY, contentW, 7, 'F');
    doc.setDrawColor(0); doc.rect(margin, curY, contentW, 7, 'S');
    doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(0);
    doc.text(`${pkLabel} : ${rec[pkField] || '\u2014'}`, margin + 3, curY + 5);
    curY += 8;

    // Metadata grid
    const sections = fmt.headerSections || [];
    const secW = contentW / Math.max(sections.length, 1);
    const metaStartY = curY;
    let maxMetaH = 0;

    for (let si = 0; si < sections.length; si++) {
      const sx = margin + si * secW;
      let sy = metaStartY + 2;
      for (const field of sections[si]) {
        const val = _getVal(rec, field.key);
        const displayVal = (isDateKey(field.key) || ['order_date','issued_date','entry_date','invoice_date','due_date','received_date'].includes(field.key))
          ? (val ? fmtDateOnlyTz(val, tz) : '') : String(val || '');
        doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(60);
        doc.text(field.label, sx + 2, sy);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(0);
        doc.text(displayVal, sx + 2 + doc.getTextWidth(field.label) + 4, sy);
        sy += 5;
      }
      maxMetaH = Math.max(maxMetaH, sy - metaStartY);
    }

    doc.setDrawColor(180); doc.rect(margin, metaStartY, contentW, maxMetaH + 1, 'S');
    for (let si = 1; si < sections.length; si++) { const dx = margin + si * secW; doc.line(dx, metaStartY, dx, metaStartY + maxMetaH + 1); }
    curY = metaStartY + maxMetaH + 3;

    // Line items table
    if (lines.length > 0) {
      const lineCols = fmt.lineColumns || [];
      const lineHead = [lineCols.map(c => c.label)];
      const lineBody = lines.map(li => lineCols.map(c => {
        if (c.pct || c.key === 'discount_pct') return fmtPct(li.discount);
        if (c.currency) return Number(li[c.key] || 0).toFixed(2);
        if (c.qty) return Number(li[c.key] || 0).toFixed(2);
        if (c.key === 'item_due_date') return li.item_due_date ? fmtDateOnlyTz(li.item_due_date, tz) : '';
        const v = li[c.key]; return v !== null && v !== undefined ? String(v) : '';
      }));

      autoTable(doc, {
        head: lineHead, body: lineBody, startY: curY,
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6.5 },
        margin: { left: margin, right: margin }, tableWidth: contentW,
        didDrawPage: () => { if (doc.internal.getCurrentPageInfo().pageNumber > pageNum) { pageNum = doc.internal.getCurrentPageInfo().pageNumber; _drawContPageHeader(doc, pageW, fullTitle, generatedAt); } },
      });
      curY = doc.lastAutoTable.finalY + 1;
    }

    // Value footer
    if (docValue !== null) {
      checkNewPage(12);
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(0);
      const valLabel = pkLabel.replace('Number', 'Value').replace('Ref', 'Value');
      doc.text(`${valLabel} (${currency}) : ${docValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin + contentW / 2 - 10, curY + 4);
      curY += 10;
    } else {
      curY += 4;
    }
  }

  _drawPageFooter(doc, pageW, pageH, pageNum, year);
  return doc;
}
